import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Nfc, CheckCircle, Loader2, AlertCircle, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { getCardShareUrl } from '../lib/cardShareUrl';

const LinkPhysicalCardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [linkState, setLinkState] = useState('idle'); // 'idle' | 'scanning' | 'writing' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  // NFC-encoded link tags scans as src=qr so they're attributed
  // to physical-card distribution in business-card analytics.
  // iter 103 — uses the unified share domain (collectiveorb.app/uplift/<id>).
  const cardUrl = `${getCardShareUrl(user?.card_decoy_id || user?.id)}?src=qr_code_scan`;
  const nfcSupported = 'NDEFReader' in window;

  const handleLinkCard = async () => {
    if (!nfcSupported) {
      toast.error('NFC is not supported on this device. Use an Android device with Chrome.');
      return;
    }

    setLinkState('scanning');
    setErrorMessage('');

    try {
      const ndef = new window.NDEFReader();
      await ndef.scan();

      toast.info('Tap your NFC card behind the camera');

      // Listen for a tag read, then write to it
      ndef.addEventListener('reading', async () => {
        setLinkState('writing');
        try {
          await ndef.write({
            records: [
              {
                recordType: 'url',
                data: cardUrl
              }
            ]
          });
          setLinkState('success');
          toast.success('Card linked successfully!');
        } catch (writeError) {
          console.error('NFC write error:', writeError);
          setErrorMessage('Failed to write to card. Try again.');
          setLinkState('error');
        }
      }, { once: true });

      // Timeout after 30 seconds
      setTimeout(() => {
        if (linkState === 'scanning') {
          setLinkState('idle');
          toast.info('NFC scan timed out. Try again.');
        }
      }, 30000);
    } catch (error) {
      console.error('NFC error:', error);
      if (error.name === 'NotAllowedError') {
        setErrorMessage('NFC permission denied. Please allow NFC access in your browser settings.');
      } else if (error.name === 'NotSupportedError') {
        setErrorMessage('NFC is not supported on this device.');
      } else {
        setErrorMessage(`NFC error: ${error.message}`);
      }
      setLinkState('error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24" data-testid="link-card-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Link your ORB NFC business card</h1>
        </div>
      </div>

      {/* Instructions */}
      <Card className="bg-white/90 dark:bg-[#141414] border-0 shadow-lg mb-6">
        <CardContent className="p-5 space-y-5">
          {[
            { step: 1, text: 'Press the Link button', icon: '1' },
            { step: 2, text: 'Tap your card behind camera', icon: '2' },
            { step: 3, text: 'Wait for app check sign', icon: '3' }
          ].map((item) => (
            <div key={item.step} className="flex items-start gap-4">
              <div className="h-8 w-8 rounded-full bg-[#586D52] flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-sm">{item.icon}</span>
              </div>
              <div className="flex-1 pt-1">
                <p className="font-medium text-sm">Step {item.step}</p>
                <p className="text-xs text-muted-foreground">{item.text}</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Status Display */}
      <div className="flex flex-col items-center py-8">
        {linkState === 'idle' && (
          <>
            <div className="h-28 w-28 rounded-full bg-white/80 dark:bg-[#1a1a1a] border-2 border-dashed border-gray-300 dark:border-gray-700 flex items-center justify-center mb-6">
              <Nfc className="w-12 h-12 text-muted-foreground" />
            </div>
            {!nfcSupported && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-3 mb-4 max-w-xs">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-yellow-700 dark:text-yellow-400">
                    NFC writing requires an Android device with Chrome browser. iOS does not support Web NFC writing.
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {linkState === 'scanning' && (
          <>
            <div className="h-28 w-28 rounded-full bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-300 dark:border-blue-700 flex items-center justify-center mb-6 animate-pulse">
              <Smartphone className="w-12 h-12 text-blue-500" />
            </div>
            <p className="text-sm font-medium text-blue-600 mb-1">Waiting for NFC card...</p>
            <p className="text-xs text-muted-foreground">Hold your card behind the phone camera</p>
          </>
        )}

        {linkState === 'writing' && (
          <>
            <div className="h-28 w-28 rounded-full bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-700 flex items-center justify-center mb-6">
              <Loader2 className="w-12 h-12 text-amber-500 animate-spin" />
            </div>
            <p className="text-sm font-medium text-amber-600">Writing to card...</p>
          </>
        )}

        {linkState === 'success' && (
          <>
            <div className="h-28 w-28 rounded-full bg-green-50 dark:bg-green-900/20 border-2 border-green-400 dark:border-green-600 flex items-center justify-center mb-6">
              <CheckCircle className="w-16 h-16 text-green-500" />
            </div>
            <p className="text-lg font-bold text-green-600 mb-1">Card Linked!</p>
            <p className="text-xs text-muted-foreground text-center max-w-xs">
              Your NFC card now links to your digital business card. Tap it on any phone to share.
            </p>
          </>
        )}

        {linkState === 'error' && (
          <>
            <div className="h-28 w-28 rounded-full bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700 flex items-center justify-center mb-6">
              <AlertCircle className="w-12 h-12 text-red-500" />
            </div>
            <p className="text-sm font-medium text-red-600 mb-1">Error</p>
            <p className="text-xs text-muted-foreground text-center max-w-xs">{errorMessage}</p>
          </>
        )}
      </div>

      {/* Link Button */}
      <div className="flex justify-center">
        <Button
          className="w-full max-w-xs bg-[#586D52] hover:bg-[#4a5d46] text-white font-semibold py-6 rounded-xl shadow-lg"
          onClick={handleLinkCard}
          disabled={linkState === 'scanning' || linkState === 'writing'}
          data-testid="link-nfc-btn"
        >
          {linkState === 'scanning' ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Scanning...
            </>
          ) : linkState === 'writing' ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Writing...
            </>
          ) : linkState === 'success' ? (
            'Link Another Card'
          ) : (
            <>
              <Nfc className="w-5 h-5 mr-2" />
              Link Card
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default LinkPhysicalCardPage;
